import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { Layout, Menu, Spin, Button, Tooltip } from 'antd'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import Cookies from 'js-cookie'
import _ from 'lodash'
import { useSelector } from 'react-redux'

import { hasVisibleChildren, isVisibleItem, isVisibleItemStrict, withPermission } from '@/annotations/nike-react'
import Header from '@/pages/layout/header/Header'
import Welcome from '@/pages/welcome/Welcome'
import Page404 from '@/components/page404/page404'
import routerConfig from '@/router/router'
import { defaultToken } from '@/utils/default'

const { Content, Sider } = Layout

// 路由生成
const generateRoute = (configList = []) =>
  _.flatten(
    configList.map(cfg =>
      _.concat(
        generateRoute(cfg.children),
        cfg.component
          ? [
              <Route
                key={cfg.key}
                path={cfg.key}
                render={withPermission(cfg.permission, cfg.permissionRule)(cfg.component)}
              />
            ]
          : []
      )
    )
  )

// 菜单组件 memo，避免父组件无关 render 触发 Menu 重算
const SideMenu = React.memo(function SideMenu({ items, openKeys, onOpenChange, onClick, selectedKey, collapsed }) {
  return (
    <Menu
      theme="dark"
      mode="inline"
      items={items}
      onClick={onClick}
      selectedKeys={[selectedKey]}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      inlineCollapsed={collapsed}
    />
  )
})

const MainLayout = () => {
  const history = useHistory()
  const location = useLocation()
  const containerRef = useRef(null)

  const spinLoading = useSelector(state => state.loadingStore.loading)
  const spinTip = useSelector(state => state.loadingStore.tip)
  const languageState = useSelector(state => state.commonStore.language)
  const deviceSize = useSelector(state => state.commonStore.deviceSize)

  const [collapsed, setCollapsed] = useState(false)

  // 尺寸派生（不再用 state + effect）
  const heightSize = 60 * deviceSize
  const siderSize = 200 * deviceSize
  const collapsedWidth = 80 * deviceSize

  // 扁平化（用于判断子菜单是否全部无权限）
  const flattenDeep = useCallback(list => {
    const out = []
    ;(function walk(arr) {
      ;(arr || []).forEach(n => {
        if (n.children) walk(n.children)
        out.push(n)
      })
    })(list)
    return out
  }, [])

  // 构建菜单（不 mutate routerConfig）
  const buildMenus = useCallback(
    (list = []) => {
      const res = []

      list.forEach(item => {
        if (!isVisibleItem(item)) return

        const next = { ...item }

        // i18n label
        if (languageState === 'zh_CN') next.label = next.labelZh
        if (languageState === 'en_US') next.label = next.labelEn
        next.title = next.label

        delete next.labelZh
        delete next.labelEn

        if (hasVisibleChildren(next)) {
          const flat = flattenDeep(next.children)

          // 若所有子菜单都无权限，则隐藏父菜单（保持你原逻辑）
          let hidden = true
          for (let i = 0; i < flat.length; i++) {
            if (isVisibleItemStrict(flat[i])) {
              hidden = false
              break
            }
          }
          next.hidden = hidden

          next.children = buildMenus(next.children || [])
          res.push(next)
        } else {
          // 无 children，且有 component 才作为菜单项
          if (next.component) {
            delete next.children
            delete next.activeMenuKeys
            res.push(next)
          }
        }
      })

      return res
    },
    [flattenDeep, languageState]
  )

  // 菜单 items：语言变更时重算
  const menusList = useMemo(() => buildMenus(routerConfig), [buildMenus])

  // 默认展开所有父菜单 key（只算一次）
  const defaultOpenKeys = useMemo(() => {
    const acc = []
    const walk = arr => {
      ;(arr || []).forEach(n => {
        if (n.children && n.children.length) {
          acc.push(n.key)
          walk(n.children)
        }
      })
    }
    walk(routerConfig)
    return acc
  }, [])

  // openKeys 受控：折叠时清空，展开恢复
  const lastOpenKeysRef = useRef(defaultOpenKeys)
  const [openKeys, setOpenKeys] = useState(defaultOpenKeys)

  useEffect(() => {
    if (collapsed) {
      lastOpenKeysRef.current = openKeys
      setOpenKeys([])
    } else {
      setOpenKeys(lastOpenKeysRef.current?.length ? lastOpenKeysRef.current : defaultOpenKeys)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed])

  const onOpenChange = useCallback(keys => {
    setOpenKeys(keys)
  }, [])

  const menuClick = useCallback(
    ({ key }) => {
      history.push(key)
    },
    [history]
  )

  const toggleCollapsed = useCallback(() => {
    setCollapsed(v => !v)
  }, [])

  // 未登录
  if (!Cookies.get(defaultToken)) return <Redirect to="/login" />

  return (
    <Layout className="app-layout">
      <Header authenticated />

      {/* 主区域：固定高度 = 视口 - header */}
      <Layout className="main-layout" style={{ height: `calc(100vh - ${heightSize}px)` }}>
        <Sider
          width={siderSize}
          collapsedWidth={collapsedWidth}
          collapsed={collapsed}
          collapsible
          trigger={null}
          className="menu-container shadow-box-right"
          style={{
            height: `calc(100vh - ${heightSize}px)`,
            position: 'relative',
            transition: 'width 200ms ease'
          }}
        >
          <SideMenu
            items={menusList}
            openKeys={openKeys}
            onOpenChange={onOpenChange}
            onClick={menuClick}
            selectedKey={location.pathname}
            collapsed={collapsed}
          />

          <Tooltip title={collapsed ? '展开菜单' : '收起菜单'} placement="right">
            <Button
              type="primary"
              shape="circle"
              size="middle"
              icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
              onClick={toggleCollapsed}
              style={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                zIndex: 10,
                width: 42,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 18px rgba(0,0,0,0.25)'
              }}
            />
          </Tooltip>
        </Sider>

        {/* 右侧内容区：让 Content 自己滚动 */}
        <Layout className="main-container" id="main-container" ref={containerRef}>
          <Content className="content-layout">
            {/* ✅ Spin 放 Content 里面，不包 Content（避免断高度链导致滚动消失） */}
            <Spin
              spinning={spinLoading}
              tip={spinTip}
              size="large"
              style={{ top: '50vh', transform: 'translateY(-50%)', zIndex: 10000 }}
            >
              <Switch>
                <Route exact path="/" component={Welcome} />
                {generateRoute(routerConfig)}
                <Route exact path="*" render={Page404} />
              </Switch>
            </Spin>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  )
}

export default MainLayout
